import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [missingProfile, setMissingProfile] = useState(false);
  const [isSigningUp, setIsSigningUp] = useState(false);

  const fetchProfile = async (userId) => {
    if (!userId) return null;
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No profile found
          if (!isSigningUp) setMissingProfile(true);
          setProfile(null);
          return null;
        } else {
          console.error('Error fetching profile:', error.message);
          return null;
        }
      } else if (data) {
        setProfile(data);
        setMissingProfile(false);
        return data;
      }
    } catch (err) {
      console.error('Unexpected profile fetch error:', err);
      return null;
    }
  };

  const refreshProfile = async () => {
    if (user) return await fetchProfile(user.id);
    return null;
  };

  useEffect(() => {
    // Initial session check
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth Event:', event);
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setProfile(null);
        if (event === 'SIGNED_OUT') setMissingProfile(false);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [isSigningUp]);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const value = {
    session,
    user,
    profile,
    loading,
    missingProfile,
    isSigningUp,
    setIsSigningUp,
    signOut,
    refreshProfile,
    setProfile,
    setMissingProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};